interface ErrorResponseBody {
  code?: unknown;
  detail?: unknown;
  versions?: unknown;
}

export interface LegalDocumentVersions {
  terms: string;
  privacy: string;
}

function isLegalDocumentVersions(value: unknown): value is LegalDocumentVersions {
  if (!value || typeof value !== "object") return false;
  const versions = value as Record<string, unknown>;
  return typeof versions.terms === "string" && typeof versions.privacy === "string";
}

/** A stable signal that current legal-document versions must be accepted. */
export class LegalAcceptanceRequired extends Error {
  readonly status = 409;
  readonly code = "legal_acceptance_required";
  readonly versions?: LegalDocumentVersions;

  constructor(
    message = "Review and accept the current legal documents before connecting a bank.",
    versions?: LegalDocumentVersions,
  ) {
    super(message);
    this.name = "LegalAcceptanceRequired";
    this.versions = versions;
  }
}

/** Map only the backend's explicit legal-version conflict. */
export function legalAcceptanceRequiredFromResponse(
  status: number | undefined,
  data: unknown,
): LegalAcceptanceRequired | null {
  if (status !== 409 || !data || typeof data !== "object") return null;
  const body = data as ErrorResponseBody;
  if (body.code !== "legal_acceptance_required") return null;
  return new LegalAcceptanceRequired(
    typeof body.detail === "string" && body.detail ? body.detail : undefined,
    isLegalDocumentVersions(body.versions) ? body.versions : undefined,
  );
}

/**
 * The server's refusal of an acceptance whose versions are no longer current.
 *
 * A tab loaded before a terms deploy posts the versions it rendered, and the
 * server rejects them (400, `{"versions": ["…Reload and review them again."]}`,
 * `LegalAcceptanceRequestSerializer.validate_versions`) because consent is only
 * recorded for what the page displayed. That refusal is correct, but the client
 * reported it as a connection problem, so retrying could never succeed and the
 * user was never told to reload (FOLLOWUP-001 T1). Returns the server's sentence
 * for that refusal and undefined for anything else, so genuine failures keep
 * their generic message.
 */
export function staleLegalVersionsMessage(error: unknown): string | undefined {
  const response = (error as { response?: { status?: unknown; data?: unknown } } | null)?.response;
  if (response?.status !== 400) return undefined;
  const data = response.data;
  if (!data || typeof data !== "object") return undefined;
  const messages = (data as ErrorResponseBody).versions;
  if (!Array.isArray(messages)) return undefined;
  const first: unknown = messages[0];
  return typeof first === "string" && first.trim() ? first : undefined;
}

/** A stable, typed signal that the backend's subscription gate rejected a request. */
export class SubscriptionRequired extends Error {
  readonly status = 402;
  readonly code = "subscription_required";

  constructor(message = "An active subscription is required to continue.") {
    super(message);
    this.name = "SubscriptionRequired";
  }
}

/**
 * The server's own refusal text, when it wrote one.
 *
 * BEN-141 review. The allocation endpoints refuse with a sentence that names
 * the number the user needs — "You have 40.00% unallocated…", "$100,000.00 is
 * more than your $3,200.00 surplus." Every caller was catching those into a
 * fixed "Couldn't save this bucket. Check the amount and try again.", so the
 * one piece of information that makes the refusal actionable never reached the
 * screen and a deliberate rejection read as a malfunction.
 *
 * Returns undefined for anything without a string `detail`, so callers keep
 * their generic fallback for genuine failures (network, 500).
 */
export function refusalDetail(error: unknown): string | undefined {
  const data = (error as { response?: { data?: unknown } } | null)?.response?.data;
  if (!data || typeof data !== "object") return undefined;
  const { detail } = data as ErrorResponseBody;
  return typeof detail === "string" && detail.trim() ? detail : undefined;
}

/** Map only the backend's explicit paywall response; other 402s stay Axios errors. */
export function subscriptionRequiredFromResponse(
  status: number | undefined,
  data: unknown,
): SubscriptionRequired | null {
  if (status !== 402 || !data || typeof data !== "object") return null;
  const body = data as ErrorResponseBody;
  if (body.code !== "subscription_required") return null;
  return new SubscriptionRequired(
    typeof body.detail === "string" && body.detail ? body.detail : undefined,
  );
}

/**
 * A response the client could not read.
 *
 * BEN-141 QF4. The forced-state pass grades what a surface renders when its own
 * data comes back unusable, and an unreadable body is the one failure axios
 * does NOT raise: a 200 whose `content-type` says JSON but whose payload did
 * not parse arrives as a plain string in `response.data`, so every adapter
 * downstream reads properties off a string and renders nothing, with no error
 * anywhere. A truncated payload, an HTML error page from a proxy, and a gzip
 * failure all land here. Turning it into a typed rejection is what lets the
 * shared failure notice tell "we could not read the answer" apart from "the
 * server said no".
 */
export class MalformedResponse extends Error {
  readonly code = "malformed_response";
  readonly url?: string;

  constructor(url?: string) {
    super("The server sent a response this app could not read.");
    this.name = "MalformedResponse";
    this.url = url;
  }
}

/** True when a JSON response body did not parse, whatever the status said. */
export function isUnparsedJsonBody(contentType: unknown, data: unknown): boolean {
  if (typeof contentType !== "string" || !contentType.toLowerCase().includes("json")) return false;
  if (typeof data !== "string") return false;
  return data.trim().length > 0;
}
